import React, { useEffect, useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { TopNavigation } from '@/components/TopNavigation'
import { Toast } from '@/components/Toast'
import { PatientId } from '@/components/PatientId'
import { ColorSquare } from '@/components/ColorSquare'
import { useAuth } from '@/hooks/useAuth'
import { UserService, UserProfileHelper, UserRole } from '@lura/shared'
import type { UserProfile } from '@lura/shared'

const USERS_PER_PAGE = 50

export const UsersPage: React.FC = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState<UserProfile[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null)
  const [resendingUserId, setResendingUserId] = useState<string | null>(null)

  // Web Admin access check
  if (!UserProfileHelper.isWebAdmin(user)) {
    return <Navigate to="/studies" replace />
  }

  const fetchUsers = async () => {
    try {
      // Only show loading spinner on initial load
      if (users.length === 0) {
        setLoading(true)
      }
      setError(null)
      const offset = (currentPage - 1) * USERS_PER_PAGE
      const roleFilters =
        selectedRoles.size > 0 ? Array.from(selectedRoles) : undefined
      const { users: fetchedUsers, totalCount: count } =
        await UserService.getAllUsers(
          searchQuery || undefined,
          roleFilters,
          USERS_PER_PAGE,
          offset
        )
      setUsers(fetchedUsers)
      setTotalCount(count)
    } catch (err) {
      console.error('Error fetching users:', err)
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [currentPage, searchQuery])

  useEffect(() => {
    if (currentPage === 1) {
      fetchUsers()
    } else {
      setCurrentPage(1)
    }
  }, [selectedRoles])

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1) // Reset to first page on new search
    fetchUsers()
  }

  const toggleRole = (role: string) => {
    const newRoles = new Set(selectedRoles)
    if (newRoles.has(role)) {
      newRoles.delete(role)
    } else {
      newRoles.add(role)
    }
    setSelectedRoles(newRoles)
    setCurrentPage(1)
  }

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      setUpdatingUserId(userId)
      setErrorMessage(null)
      setSuccessMessage(null)

      // Set the user to have only this role
      await UserService.updateUserRoles(userId, [newRole])

      // Refresh users list
      await fetchUsers()

      const userProfile = users.find(u => u.id === userId)
      const userName = userProfile
        ? `${userProfile.first_name} ${userProfile.last_name}`
        : 'User'

      setSuccessMessage(`${userName}'s role updated to ${newRole}`)
    } catch (err) {
      console.error('Error updating user role:', err)
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to update user role'
      )
    } finally {
      setUpdatingUserId(null)
    }
  }

  const handleResendVerification = async (userId: string, userName: string) => {
    try {
      setResendingUserId(userId)
      setErrorMessage(null)
      setSuccessMessage(null)

      await UserService.resendVerificationEmail(userId)

      setSuccessMessage(`Verification email resent to ${userName}`)
    } catch (err) {
      console.error('Error resending verification email:', err)
      setErrorMessage(
        err instanceof Error
          ? err.message
          : 'Failed to resend verification email'
      )
    } finally {
      setResendingUserId(null)
    }
  }

  const totalPages = Math.ceil(totalCount / USERS_PER_PAGE)

  return (
    <div className="min-h-screen bg-off-white">
      <TopNavigation
        currentPage="Users"
        onSignOut={handleSignOut}
        user={user}
      />

      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="px-4 pb-6 sm:px-0">
          {/* Page Header */}
          <div className="flex justify-between items-center mb-8">
            <h2
              className="text-5xl font-normal text-dark-blue"
              style={{ fontFamily: 'Family' }}
            >
              Users
            </h2>
          </div>

          {/* Search and Filter */}
          <div className="mb-6 space-y-4">
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name or email..."
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
              />
              <button
                type="submit"
                className="px-6 py-2.5 bg-dark-blue text-white rounded-2xl hover:bg-opacity-90 transition-colors"
              >
                Search
              </button>
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('')
                    setCurrentPage(1)
                  }}
                  className="px-6 py-2.5 border border-gray-300 rounded-2xl hover:bg-gray-50 transition-colors"
                >
                  Clear
                </button>
              )}
            </form>

            {/* Role Filter Buttons */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">
                Filter by Role:
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleRole(UserRole.Patient)}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    selectedRoles.has(UserRole.Patient)
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Patient
                </button>
                <button
                  onClick={() => toggleRole(UserRole.Clinician)}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    selectedRoles.has(UserRole.Clinician)
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Clinician
                </button>
                <button
                  onClick={() => toggleRole(UserRole.StudyAdmin)}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    selectedRoles.has(UserRole.StudyAdmin)
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Study Admin
                </button>
                <button
                  onClick={() => toggleRole(UserRole.WebAdmin)}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    selectedRoles.has(UserRole.WebAdmin)
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Web Admin
                </button>
              </div>
            </div>
          </div>

          {/* Error State for page-level errors (loading data) */}
          {error && (
            <div className="mb-6 rounded-3xl bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State - only show on initial load */}
          {loading && users.length === 0 && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          )}

          {/* Users Table - keep visible once loaded */}
          {!error && users.length > 0 && (
            <>
              <div className="bg-white shadow-sm rounded-3xl overflow-hidden mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    All Users
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    All users in the system
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-highlight-green">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                        >
                          User
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                        >
                          Studies
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                        >
                          Email Status
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                        >
                          Last Login
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                        >
                          Role
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-6 py-12 text-center text-sm text-gray-500"
                          >
                            {searchQuery
                              ? 'No users found matching your search'
                              : 'No users in the system'}
                          </td>
                        </tr>
                      ) : (
                        users.map(userProfile => {
                          const hasMultipleRoles = userProfile.roles.length > 1
                          const primaryRole =
                            userProfile.roles[0] || UserRole.Patient

                          return (
                            <tr
                              key={userProfile.id}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                {userProfile.roles.includes(UserRole.Patient) ? (
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      <PatientId patientId={userProfile.id} size="sm" />
                                    </div>
                                    <div className="text-sm text-gray-900">
                                      {userProfile.first_name} {userProfile.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {userProfile.email}
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {userProfile.first_name} {userProfile.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {userProfile.email}
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <div className="space-y-1">
                                  {userProfile.studies &&
                                    userProfile.studies.length > 0 &&
                                    userProfile.studies.map(study => (
                                      <div
                                        key={study.id}
                                        className="flex items-center gap-2"
                                      >
                                        <ColorSquare
                                          color={study.color}
                                          size="sm"
                                        />
                                        <span className="text-sm text-gray-900">
                                          {study.title}
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {userProfile.email_confirmed_at ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Confirmed
                                  </span>
                                ) : (
                                  <button
                                    onClick={e => {
                                      e.stopPropagation()
                                      const displayName = userProfile.roles.includes(UserRole.Patient)
                                        ? UserProfileHelper.getAnonymizedId(userProfile.id)
                                        : `${userProfile.first_name} ${userProfile.last_name}`
                                      handleResendVerification(userProfile.id, displayName)
                                    }}
                                    disabled={resendingUserId === userProfile.id}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {resendingUserId === userProfile.id ? (
                                      'Sending...'
                                    ) : (
                                      <>Unconfirmed • Resend</>
                                    )}
                                  </button>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {userProfile.last_sign_in_at ? (
                                  <div className="text-sm text-gray-900">
                                    {new Date(
                                      userProfile.last_sign_in_at
                                    ).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-400">
                                    Never
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <select
                                    value={primaryRole}
                                    onChange={e =>
                                      handleRoleChange(
                                        userProfile.id,
                                        e.target.value
                                      )
                                    }
                                    disabled={updatingUserId === userProfile.id}
                                    className="block w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <option value={UserRole.Patient}>
                                      Patient
                                    </option>
                                    <option value={UserRole.Clinician}>
                                      Clinician
                                    </option>
                                    <option value={UserRole.StudyAdmin}>
                                      Study Admin
                                    </option>
                                    <option value={UserRole.WebAdmin}>
                                      Web Admin
                                    </option>
                                  </select>
                                  {hasMultipleRoles && (
                                    <div
                                      className="flex-shrink-0 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                                      title="User has multiple roles assigned"
                                    >
                                      <svg
                                        className="w-3 h-3 text-white"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {(currentPage - 1) * USERS_PER_PAGE + 1} to{' '}
                    {Math.min(currentPage * USERS_PER_PAGE, totalCount)} of{' '}
                    {totalCount} users
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-2xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-2">
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum
                          if (totalPages <= 5) {
                            pageNum = i + 1
                          } else if (currentPage <= 3) {
                            pageNum = i + 1
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i
                          } else {
                            pageNum = currentPage - 2 + i
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-4 py-2 border rounded-2xl text-sm font-medium transition-colors ${
                                currentPage === pageNum
                                  ? 'bg-dark-blue text-white border-dark-blue'
                                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          )
                        }
                      )}
                    </div>
                    <button
                      onClick={() =>
                        setCurrentPage(p => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-2xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Toast Notifications */}
      <Toast
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />
      <Toast
        message={errorMessage}
        type="error"
        onClose={() => setErrorMessage(null)}
      />
    </div>
  )
}
